import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { KpiStrip, type KpiItem } from '../components/KpiTile';
import { StatusPill, SentPill } from '../components/StatusPill';
import { EmptyState } from '../components/EmptyState';
import { FilterSidebar } from '../components/FilterSidebar';
import { FilterChips } from '../components/FilterChips';
import { ColumnMenu } from '../components/ColumnMenu';
import { RowDrawer } from '../components/RowDrawer';
import { RecordSendModal } from '../modals/RecordSendModal';
import { getReport, listReportRows, downloadReport, downloadFiltered } from '../lib/api';
import { cellFlag, enrichedColSet, rowNeedsReview } from '../lib/review';
import { COLS } from '../mocks/columns';
import { EMPTY_FILTERS, type ColumnSpec, type ReportFilters, type ReportRow, type SentStatus } from '../types';
import { fmt } from '../lib/format';

interface SortState {
  col: keyof ReportRow;
  dir: 'asc' | 'desc';
}

export function ReportDetail(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const reportQ = useQuery({ queryKey: ['report', id], queryFn: () => getReport(id) });
  const rowsQ = useQuery({ queryKey: ['report-rows', id], queryFn: () => listReportRows(id) });

  const [filters, setFilters] = useState<ReportFilters>(EMPTY_FILTERS);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [sort, setSort] = useState<SortState>({ col: 'dateInc', dir: 'desc' });
  const [drawerRow, setDrawerRow] = useState<ReportRow | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showRecordSend, setShowRecordSend] = useState(false);
  const [colMenu, setColMenu] = useState(false);
  const [colVisible, setColVisible] = useState<Record<string, boolean>>(() => {
    const v: Record<string, boolean> = {};
    COLS.forEach((c) => { v[c.id] = true; });
    return v;
  });

  const rows = rowsQ.data ?? [];

  const filtered = useMemo(() => {
    let r = rows.slice();
    if (filters.account.length) r = r.filter((x) => x.account && filters.account.includes(x.account));
    if (filters.affiliate.length) r = r.filter((x) => x.affiliate && filters.affiliate.includes(x.affiliate));
    if (filters.status.length) r = r.filter((x) => filters.status.includes(x.status));
    if (filters.coachee) r = r.filter((x) => (x.coachee ?? '').toLowerCase().includes(filters.coachee.toLowerCase()));
    if (filters.opp) r = r.filter((x) => (x.oppNum ?? '').toLowerCase().includes(filters.opp.toLowerCase()));
    if (filters.sent.length) r = r.filter((x) => filters.sent.includes(x.sentStatus ?? 'unsent'));
    r.sort((a, b) => {
      const av = a[sort.col];
      const bv = b[sort.col];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return r;
  }, [rows, filters, sort]);

  const hasFilters = filtered.length !== rows.length;
  const report = reportQ.data;
  const unsentCount = useMemo(
    () => rows.filter((r) => (r.sentStatus ?? 'unsent') === 'unsent').length,
    [rows],
  );
  const reviewCount = useMemo(() => rows.filter(rowNeedsReview).length, [rows]);
  // `reviewOnly` is a view-only toggle (not sent to the download endpoint), so it
  // narrows what's shown without changing the sidebar-filter download payload.
  const visible = useMemo(
    () => (reviewOnly ? filtered.filter(rowNeedsReview) : filtered),
    [filtered, reviewOnly],
  );

  const kpis: KpiItem[] = report ? [
    { label: 'Sessions completed', value: fmt.num(report.sessions_completed) || '—' },
    { label: 'Total hours incurred', value: fmt.hrs(report.total_hours_incurred) || '—', unit: 'h' },
    { label: 'Total fees budgeted', value: fmt.curK(report.total_fee_budget) || '—' },
    { label: 'Active engagements', value: fmt.num(report.active_engagements) || '—' },
  ] : [];

  const visCols: ColumnSpec[] = COLS.filter((c) => colVisible[c.id] !== false || c.frozen);
  let leftOffset = 0;
  const colOffsets: Record<string, number> = {};
  for (const c of visCols) {
    if (c.frozen) {
      colOffsets[c.id] = leftOffset;
      leftOffset += c.w;
    }
  }

  const toggleSort = (col: keyof ReportRow) => {
    if (sort.col === col) setSort({ col, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    else setSort({ col, dir: 'desc' });
  };

  const totals = useMemo(() => ({
    hours: visible.reduce((s, r) => s + (r.hours ?? 0), 0),
    fee: visible.reduce((s, r) => s + (r.feeBudget ?? 0), 0),
    hBudget: visible.reduce((s, r) => s + (r.hBudget ?? 0), 0),
    hIncurred: visible.reduce((s, r) => s + (r.hIncurred ?? 0), 0),
  }), [visible]);

  if (reportQ.isLoading) {
    return (
      <div className="ari-app">
        <TopNav />
        <div className="page" style={{ alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
          <div className="spinner dark" />
        </div>
      </div>
    );
  }

  if (report?.status === 'Running') {
    return (
      <div className="ari-app">
        <TopNav />
        <div className="page">
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--paper)' }}>
            <div style={{ textAlign: 'center', maxWidth: 460 }}>
              <div className="eb" style={{ color: 'var(--accent)' }}>Generation in progress</div>
              <h2 className="d-title" style={{ fontSize: 38, marginTop: 8 }}>Pulling sessions…</h2>
              <p style={{ marginTop: 16, color: 'var(--mute)', fontSize: 14, lineHeight: 1.55 }}>
                Salesforce query is running. This usually takes ~10 seconds. We'll refresh this page when it's ready.
              </p>
              <div style={{ marginTop: 24, width: 320, margin: '24px auto 0' }}>
                <div className="progbar" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (report?.status === 'Failed') {
    return (
      <div className="ari-app">
        <TopNav />
        <div className="page">
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--paper)' }}>
            <div style={{ textAlign: 'center', maxWidth: 460 }}>
              <div className="eb" style={{ color: 'var(--risk)' }}>Generation failed</div>
              <h2 className="d-title" style={{ fontSize: 38, marginTop: 8 }}>This report didn't finish</h2>
              <p style={{ marginTop: 16, color: 'var(--mute)', fontSize: 14, lineHeight: 1.55 }}>
                Most often this resolves within a minute. Check the error log for details or trigger a retry.
              </p>
              <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'center' }}>
                <Button variant="secondary" icon="eye">View error log</Button>
                <Button variant="primary" icon="refresh">Retry generation</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <div className="page-hd" style={{ paddingBottom: 24 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="crumb">
              <Link to="/reports">Reports</Link>
              <span style={{ margin: '0 6px', color: 'var(--border-2)' }}>/</span>
              <span>{report?.id ?? id}</span>
            </div>
            <div className="eb">Weekly report</div>
            <h1 className="d-title" style={{ marginTop: 2 }}>
              {report ? `Week of ${fmt.range(report.rangeStart, report.rangeEnd)}` : 'Report'}
            </h1>
            {report && (
              <div className="d-meta" style={{ marginTop: 10, fontSize: 13 }}>
                Generated <span className="d-tnum">{fmt.dateTime(report.generatedAt)}</span> by {report.user}
                <span style={{ margin: '0 10px', color: 'var(--border-2)' }}>·</span>
                <span className="d-tnum">{fmt.num(report.rows)} rows</span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Button variant="secondary" icon="arrowUp" onClick={() => setShowRecordSend(true)}>
              Record send
            </Button>
            <Button
              variant="secondary"
              icon="download"
              disabled={unsentCount === 0}
              title="Builds an .xlsx of only the sessions finance hasn't received yet"
              onClick={() => downloadFiltered(id, { sentStatuses: ['unsent'] })}
            >
              Download unsent ({fmt.num(unsentCount)})
            </Button>
            <Button
              variant="secondary"
              icon="download"
              disabled={!hasFilters}
              onClick={() => downloadFiltered(id, {
                accounts: filters.account.length ? filters.account : undefined,
                affiliates: filters.affiliate.length ? filters.affiliate : undefined,
                statuses: filters.status.length ? filters.status : undefined,
                coachee: filters.coachee || undefined,
                oppNumber: filters.opp || undefined,
                sentStatuses: filters.sent.length ? filters.sent : undefined,
              })}
            >
              {hasFilters ? `Download filtered (${filtered.length} rows)` : 'Download filtered'}
            </Button>
            <Button variant="primary" icon="download" onClick={() => downloadReport(id)}>
              Download .xlsx
            </Button>
          </div>
        </div>

        <KpiStrip items={kpis} />

        <div className="page-body" style={{ flexDirection: 'row' }}>
          <FilterSidebar
            rows={rows}
            filters={filters}
            setFilters={setFilters}
            collapsed={collapsed}
            onToggle={() => setCollapsed(!collapsed)}
          />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
            <div className="toolbar">
              <div className="left">
                {hasFilters ? (
                  <FilterChips filters={filters} setFilters={setFilters} />
                ) : (
                  <>
                    <Icon name="search" size={14} style={{ color: 'var(--mute)' }} />
                    <input className="input" placeholder="Search all columns…" style={{ width: 280, border: '1px solid var(--border-1)' }} />
                  </>
                )}
              </div>
              <div className="right">
                <span className="count">
                  <strong>{fmt.num(visible.length)}</strong> of {fmt.num(rows.length)} sessions
                  {(hasFilters || reviewOnly) && <span> · filtered</span>}
                </span>
                {reviewCount > 0 && (
                  <Button
                    size="sm"
                    variant={reviewOnly ? 'primary' : 'ghost'}
                    icon="alert"
                    onClick={() => setReviewOnly((v) => !v)}
                    title="Show only rows that need review — backfilled values to verify, or required fields still missing"
                  >
                    Needs review ({fmt.num(reviewCount)})
                  </Button>
                )}
                <div style={{ width: 1, height: 18, background: 'var(--border-1)', margin: '0 6px' }} />
                <Button size="sm" variant="ghost" icon="cog" onClick={() => setColMenu(!colMenu)}>Columns</Button>
              </div>
              {colMenu && <ColumnMenu visible={colVisible} setVisible={setColVisible} />}
            </div>

            {reviewCount > 0 && (
              <div className="review-legend">
                <span><i className="rl-dot rl-enriched" />Backfilled — verify</span>
                <span><i className="rl-dot rl-missing" />Missing — blocks NetSuite load</span>
                <span><i className="rl-dot rl-verify" />Incomplete / check — complete in Salesforce</span>
              </div>
            )}

            {rowsQ.isLoading ? (
              <div style={{ padding: 32 }}><div className="spinner dark" /></div>
            ) : visible.length === 0 ? (
              <EmptyState
                icon="search"
                title="No sessions match these filters"
                body={`Of ${fmt.num(rows.length)} rows, none satisfy your filters. Try widening the date range or clearing a chip.`}
                cta={<Button variant="secondary" onClick={() => { setFilters(EMPTY_FILTERS); setReviewOnly(false); }}>Clear filters</Button>}
              />
            ) : (
              <div className="tbl-wrap">
                <table
                  className="tbl"
                  style={{ tableLayout: 'fixed', width: visCols.reduce((s, c) => s + c.w, 0) }}
                >
                  <colgroup>
                    {visCols.map((c) => <col key={c.id} style={{ width: c.w }} />)}
                  </colgroup>
                  <thead>
                    <tr>
                      {visCols.map((c, i) => {
                        const isLastFrozen = c.frozen && visCols[i + 1] && !visCols[i + 1].frozen;
                        const cls = [
                          c.align === 'right' ? 'num' : '',
                          c.align === 'center' ? 'center' : '',
                          'sortable',
                          c.frozen ? 'frz' : '',
                          isLastFrozen ? 'last' : '',
                        ].filter(Boolean).join(' ');
                        return (
                          <th
                            key={c.id}
                            className={cls}
                            style={c.frozen ? { left: colOffsets[c.id] } : undefined}
                            onClick={() => toggleSort(c.id)}
                          >
                            {c.label}
                            {sort.col === c.id && <span className="sortarr">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => {
                      const enriched = enrichedColSet(row);
                      return (
                      <tr
                        key={row.id}
                        className={drawerRow?.id === row.id ? 'selected' : undefined}
                        onClick={() => setDrawerRow(row)}
                      >
                        {visCols.map((c, i) => {
                          const isLastFrozen = c.frozen && visCols[i + 1] && !visCols[i + 1].frozen;
                          const flag = cellFlag(c.id, row, enriched);
                          const cls = [
                            c.type === 'cur' ? 'cur' : '',
                            c.type === 'num' ? 'num' : '',
                            c.type === 'id' ? 'id num' : '',
                            c.type === 'date' ? 'center' : '',
                            c.align === 'center' && c.type !== 'date' ? 'center' : '',
                            c.frozen ? 'frz' : '',
                            isLastFrozen ? 'last' : '',
                            flag ? `cell-${flag}` : '',
                          ].filter(Boolean).join(' ');
                          return (
                            <td
                              key={c.id}
                              className={cls}
                              style={c.frozen ? { left: colOffsets[c.id] } : undefined}
                              title={typeof row[c.id] === 'string' ? (row[c.id] as string) : undefined}
                            >
                              {renderCell(c, row)}
                            </td>
                          );
                        })}
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      {visCols.map((c, i) => {
                        const isLastFrozen = c.frozen && visCols[i + 1] && !visCols[i + 1].frozen;
                        let content: string = '';
                        if (i === 0) content = `${visible.length} sessions${(hasFilters || reviewOnly) ? ' · filtered' : ''}`;
                        else if (c.id === 'hours') content = fmt.hrs(totals.hours) + ' h';
                        else if (c.id === 'feeBudget') content = fmt.cur(totals.fee);
                        else if (c.id === 'hIncurred') content = fmt.hrs(totals.hIncurred) + ' h';
                        else if (c.id === 'hBudget') content = fmt.hrs(totals.hBudget) + ' h';
                        const cls = [
                          c.type === 'cur' ? 'cur' : '',
                          c.type === 'num' ? 'num' : '',
                          c.frozen ? 'frz' : '',
                          isLastFrozen ? 'last' : '',
                        ].filter(Boolean).join(' ');
                        return (
                          <td
                            key={c.id}
                            className={cls}
                            style={c.frozen ? { left: colOffsets[c.id], background: 'var(--paper-2)' } : undefined}
                          >
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {drawerRow && <RowDrawer row={drawerRow} onClose={() => setDrawerRow(null)} />}
        </div>
      </div>
      {showRecordSend && (
        <RecordSendModal
          onClose={() => setShowRecordSend(false)}
          onRecorded={() => {
            qc.invalidateQueries({ queryKey: ['report-rows'] });
            qc.invalidateQueries({ queryKey: ['send-batches'] });
          }}
        />
      )}
      {/* Avoid unused-var warning from onNav refactor */}
      <span style={{ display: 'none' }}>{navigate.length}</span>
    </div>
  );
}

function renderCell(col: ColumnSpec, row: ReportRow): JSX.Element | string | number {
  // Sent badge renders even when the stamp is absent (absent ≡ unsent).
  if (col.id === 'sentStatus') return <SentPill value={(row.sentStatus ?? 'unsent') as SentStatus} />;
  const v = row[col.id];
  if (v == null || v === '') return <span className="em">—</span>;
  switch (col.type) {
    case 'date':
      return <span className="d-tnum">{fmt.date(v as string)}</span>;
    case 'cur':
      return fmt.curFull(v as number);
    case 'num':
      if (col.id === 'hIncurred' && row.hBudget && (v as number) / row.hBudget >= 1.0) {
        const w = Math.min(100, ((v as number) / row.hBudget) * 50);
        return (
          <span
            className="hbar"
            style={{ ['--w' as string]: `${w}%` } as React.CSSProperties}
          >
            {fmt.hrs(v as number)}
          </span>
        );
      }
      return fmt.hrs(v as number);
    case 'id':
      return String(v);
    case 'pill':
      return <StatusPill value={String(v)} />;
    default:
      return String(v);
  }
}
